/*!
 * vu.js — vu(): meters, to see how loud things are
 * © 2026 Rob Canning — GPLv3
 *
 *     vu()                 the whole mix as it is heard: left and right
 *     vu(bass, drums)      a bar for each sound named
 *     vu(all)              a bar for each sound playing, as they come and go
 *     vu(master, bass)     the mix and a sound side by side
 *     vu(bass, uid:m2)     another set of meters beside the first (vu is the name otherwise)
 *     vu(floor:-48)        the scale's bottom in dB (-60); the top is +6
 *     vu(hold:3)           beats the peak mark stays before it falls (1.5 s)
 *     vu(bass, size:[0.04, 0.3], at:[0.97, 0.5])   its box, as viz() takes them:
 *                          fractions of the screen (width, height; centre)
 *     vu(stop)             take them away (vu(uid:m2, stop:1) one set)
 *
 * Each bar is the sound's level (RMS, rising at once, falling over about
 * 0.3 s); the line above it is the peak, held, then falling; the number
 * on top is the loudest peak so far, in dB. Over 0 dB (full scale) the
 * number and the bar's top turn red: that is clipping, heard as crackle.
 *
 * The meters are drawn in a box like a sound's: drag it, pull its edges,
 * or viz(vu, size:[0.2, 0.5], at:[0.9, 0.5], fill:#000). Running the line
 * again changes what it shows and leaves the box where it is. hush()
 * leaves them up -- silence is worth seeing.
 *
 * A synth is metered at its output (after pan, delay and reverb), an audio
 * file after its gain and pan; the mix after VOL and the limiter (what
 * capture() records). Several voices of one sound (poly, pool) show as the
 * loudest.
 *
 *     vu(s3, stereo)       s3 as a pair of bars, left and right, its name under both
 *     vu(all, stereo)      every sound as a pair
 *     synth(uid:s3, ..., vu:stereo)   s3 a pair wherever it is metered, vu(all) too;
 *                                     vu:mono one bar, even in vu(all, stereo)
 *
 * A bar a sound: one, unless asked for two. A sound asked for two that
 * comes out in mono (no pan:, no reverb, a one-channel file) shows the same
 * on both.
 */

import { createLiveAudioRect, removeLiveAudioRect, placeLiveRect } from "./audio/audioFile.js";
import { masterMix } from "./audio/audioShared.js";
import { activeSynthVoices, activeAudioCues } from "./audio/voices.js";
import { levelOf } from "./audio/level.js";
import { secs } from "./beats.js";
import { vuModeOf } from "./audio/vuModes.js";

const SVG_NS = "http://www.w3.org/2000/svg";
const TOP_DB = 6;
const RELEASE_S = 0.3;            // the bar falls by 1/e in this long
const PEAK_FALL_DB_S = 20;        // after the hold, the peak mark falls this fast
const ZONES = [[-Infinity, -18, "#3c9"], [-18, -6, "#ec3"], [-6, Infinity, "#f43"]];

const meters = new Map();         // uid -> { rect, g, sources, bars, opts }
let raf = 0;

const dbOf = v => (v > 1e-6 ? 20 * Math.log10(v) : -120);

// ---- what is measured ---------------------------------------------------

let master = null;                // { left, right } analysers on the mix
function masterTaps() {
  if (master) return master;
  const ctx = masterMix.context;
  const split = ctx.createChannelSplitter(2);
  const tap = () => { const a = ctx.createAnalyser(); a.fftSize = 2048; return a; };
  master = { left: tap(), right: tap() };
  masterMix.connect(split);
  split.connect(master.left, 0);
  split.connect(master.right, 1);
  return master;
}

const taps = new WeakMap();       // a voice's output node -> { mono, left, right } analysers
/**
 * The analysers on a voice's output: the whole of it (an analyser mixes
 * what it hears to one), and its left and right apart, made when first
 * asked for. Only read: an analyser leads nowhere.
 */
function tapsOf(node) {
  let t = taps.get(node);
  if (!t) { t = {}; taps.set(node, t); }
  const make = () => { const a = node.context.createAnalyser(); a.fftSize = 2048; return a; };
  return {
    get mono() { if (!t.mono) { t.mono = make(); node.connect(t.mono); } return t.mono; },
    side(i) {
      if (!t.split) {
        t.split = node.context.createChannelSplitter(2);
        node.connect(t.split);
        t.left = make(); t.right = make();
        t.split.connect(t.left, 0); t.split.connect(t.right, 1);
      }
      return i ? t.right : t.left;
    },
  };
}

/**
 * Every voice of the sound `name` (name, name__2 ...): its output node, and
 * whether it comes out in stereo -- a synth panned (pan:) or in a room
 * (reverb:, a stereo response); a file of two channels, panned, or in a room.
 */
function voicesOf(name) {
  const out = [];
  const mine = k => k === name || String(k).startsWith(`${name}__`);
  for (const [k, v] of window.activeSynthVoices || []) {
    const node = v?.graph?.sum;
    if (mine(k) && node) out.push({ node, stereo: !!(v.graph.panner || v.graph.reverb) });
  }
  for (const [k, v] of window.activeAudioCues || []) {
    if (!mine(k)) continue;
    const node = v?.panNode || v?.gainNode;
    const stereo = !!(v?.panNode || v?._live?.buf?.numberOfChannels > 1 || v?.effectsChain?.reverb);
    if (node) out.push({ node, stereo });
    else if (v?.analyser) out.push({ analyser: v.analyser, stereo: false });
  }
  return out;
}

/** The analysers to read for a sound: the whole (side null), or its left (0) / right (1). */
function readers(voices, side) {
  return voices.map(v => {
    if (v.analyser) return v.analyser;
    const t = tapsOf(v.node);
    // a mono voice in a stereo pair is heard on both sides alike
    return side == null || !v.stereo ? t.mono : t.side(side);
  });
}

/** Every sound playing now, by name. */
function soundsPlaying() {
  const names = new Set();
  for (const k of activeSynthVoices.keys()) names.add(String(k).split("__")[0]);
  for (const k of activeAudioCues.keys()) names.add(String(k).split("__")[0]);
  return [...names];
}

let buf = null;
/** { rms, peak } of what the analysers hold now: the loudest of them. */
function measure(analysers) {
  let rms = 0, peak = 0;
  for (const a of analysers) {
    if (!buf || buf.length !== a.fftSize) buf = new Float32Array(a.fftSize);
    a.getFloatTimeDomainData(buf);
    let s = 0, p = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = buf[i];
      s += v * v;
      if (v > p) p = v; else if (-v > p) p = -v;
    }
    rms = Math.max(rms, Math.sqrt(s / buf.length));
    peak = Math.max(peak, p);
  }
  return { rms, peak };
}

/**
 * The bars a set of meters shows: [{ key, sound, side, get() }] -- one for a
 * mono sound, a pair (side "L", "R") for a stereo one and for the mix.
 */
function barsFor(sources, stereo = false) {
  const out = [];
  const sound = n => {
    // its own line's vu:stereo / vu:mono, else this vu line's stereo, else one bar
    if ((vuModeOf(n) ?? (stereo ? "stereo" : "mono")) === "stereo") {
      out.push({ key: `${n}\tL`, sound: n, side: "L", get: () => readers(voicesOf(n), 0) },
        { key: `${n}\tR`, sound: n, side: "R", get: () => readers(voicesOf(n), 1) });
    } else out.push({ key: n, sound: n, side: null, get: () => readers(voicesOf(n), null) });
  };
  for (const s of sources) {
    if (s === "master") {
      out.push({ key: "master\tL", sound: "mix", side: "L", master: true, get: () => [masterTaps().left] },
        { key: "master\tR", sound: "mix", side: "R", master: true, get: () => [masterTaps().right] });
    } else if (s === "all") {
      for (const n of soundsPlaying()) sound(n);
    } else sound(s);
  }
  return out;
}

// ---- drawing ------------------------------------------------------------

const el = (tag, attrs = {}, parent = null) => {
  const e = document.createElementNS(SVG_NS, tag);
  for (const k in attrs) e.setAttribute(k, attrs[k]);
  e.style.pointerEvents = "none";     // the box under it takes the drag (a stylesheet beats the attribute)
  parent?.appendChild(e);
  return e;
};

/** A bar's drawing, made once and moved each frame. */
function barShapes(g) {
  const b = { group: el("g", {}, g) };
  b.trough = el("rect", { fill: "#ffffff14" }, b.group);
  b.zones = ZONES.map(([, , fill]) => el("rect", { fill }, b.group));
  b.zero = el("line", { stroke: "#ffffff60" }, b.group);
  b.peak = el("line", { stroke: "#fff" }, b.group);
  b.max = el("text", { "text-anchor": "middle", "font-family": "monospace", fill: "#ccc" }, b.group);
  b.label = el("text", { "text-anchor": "middle", "font-family": "monospace", fill: "#aaa" }, b.group);
  // under a stereo pair, the left bar carries the sound's name for both
  b.pair = el("text", { "text-anchor": "middle", "font-family": "monospace", fill: "#aaa" }, b.group);
  b.level = -120; b.peakDb = -120; b.peakAt = 0; b.maxDb = -120;
  return b;
}

function drawMeter(m, now, dt) {
  const { rect, g, opts } = m;
  // ride with the box: its place, its drag, whatever turns it
  if (g.parentNode !== rect.parentNode || g.previousSibling !== rect) rect.after(g);
  const t = rect.getAttribute("transform");
  if (t) g.setAttribute("transform", t); else g.removeAttribute("transform");

  const x = +rect.getAttribute("x"), y = +rect.getAttribute("y");
  const w = +rect.getAttribute("width"), h = +rect.getAttribute("height");
  // its own dark ground, to read on any score -- unless viz(vu, fill:) gave one
  const own = (rect.getAttribute("fill") || "transparent") === "transparent";
  m.ground.style.display = own ? "" : "none";
  for (const [k, v] of Object.entries({ x, y, width: w, height: h, rx: Math.min(w, h) * 0.04 })) m.ground.setAttribute(k, v);

  // the bars: for vu(all), the sounds playing now
  const want = barsFor(m.sources, m.opts.stereo);
  const key = want.map(b => b.key).join("\n");
  if (key !== m.key) {
    const old = new Map((m.bars || []).map(b => [b.key, b.shapes]));
    m.bars = want.map(b => ({ ...b, shapes: old.get(b.key) || barShapes(g) }));
    for (const [k, s] of old) if (!m.bars.some(b => b.key === k)) s.group.remove();
    m.key = key;
  } else m.bars = m.bars.map((b, i) => ({ ...want[i], shapes: b.shapes }));

  const n = Math.max(1, m.bars.length);
  const pad = Math.min(w, h) * 0.06;
  const text = Math.max(4, Math.min(h * 0.07, (w - 2 * pad) / n * 0.3));
  // a second line of labels when a pair has its name under its L and R
  const pairs = m.bars.some(b => b.side);
  const top = y + pad + text * 1.4, bottom = y + h - pad - text * (pairs ? 2.6 : 1.4);
  const slot = (w - 2 * pad) / n, bw = slot * 0.42;           // slim bars, air between
  const floor = opts.floor;
  const yOf = db => bottom - (Math.max(floor, Math.min(TOP_DB, db)) - floor) / (TOP_DB - floor) * (bottom - top);
  const fallDb = -8.686 * dt / RELEASE_S;          // e^(-dt/RELEASE_S), in dB

  m.bars.forEach((bar, i) => {
    const s = bar.shapes;
    // a sound is read before its fader: the fader (level()) is applied here
    const fader = bar.master ? 1 : levelOf(bar.sound);
    const m0 = measure(bar.get()), rms = m0.rms * fader, peak = m0.peak * fader;
    // the bar: up at once, down over RELEASE_S
    const lv = dbOf(rms);
    s.level = Math.max(lv, s.level + fallDb);
    // the peak mark: held, then falling
    const pk = dbOf(peak);
    if (pk >= s.peakDb) { s.peakDb = pk; s.peakAt = now; }
    else if (now - s.peakAt > opts.hold * 1000) s.peakDb = Math.max(pk, s.peakDb - PEAK_FALL_DB_S * dt);
    s.maxDb = Math.max(s.maxDb, pk);

    const bx = x + pad + i * slot + (slot - bw) / 2, cx = bx + bw / 2;
    const attrs = (e, a) => { for (const k in a) e.setAttribute(k, a[k]); };
    attrs(s.trough, { x: bx, y: top, width: bw, height: bottom - top });
    ZONES.forEach(([lo, hi], z) => {
      const y0 = yOf(Math.max(lo, floor)), y1 = yOf(Math.min(hi, s.level));
      const r = s.zones[z];
      r.setAttribute("x", bx); r.setAttribute("width", bw);
      r.setAttribute("y", Math.min(y0, y1)); r.setAttribute("height", Math.max(0, y0 - y1));
    });
    const z0 = yOf(0);
    attrs(s.zero, { x1: bx - bw * 0.15, x2: bx + bw * 1.15, y1: z0, y2: z0 });
    const py = yOf(s.peakDb);
    const showPeak = s.peakDb > floor;
    attrs(s.peak, { x1: bx, x2: bx + bw, y1: py, y2: py });
    s.peak.setAttribute("stroke-width", Math.max(0.5, text * 0.15));
    s.peak.setAttribute("stroke", s.peakDb > 0 ? "#f43" : "#fff");
    s.peak.style.display = showPeak ? "" : "none";
    s.zero.setAttribute("stroke-width", Math.max(0.3, text * 0.06));

    s.max.setAttribute("x", cx); s.max.setAttribute("y", top - text * 0.4);
    s.max.setAttribute("font-size", text);
    s.max.setAttribute("fill", s.maxDb > 0 ? "#f43" : "#ccc");
    s.max.textContent = s.maxDb > floor ? (s.maxDb > 0 ? "+" : "") + s.maxDb.toFixed(Math.abs(s.maxDb) < 10 ? 1 : 0) : "—";
    s.label.setAttribute("x", cx); s.label.setAttribute("y", bottom + text * 1.15);
    s.label.setAttribute("font-size", text);
    s.label.textContent = bar.side ?? bar.sound;
    // the pair's name, centred under both bars, on the left one
    const pairName = bar.side === "L";
    s.pair.style.display = pairName ? "" : "none";
    if (pairName) {
      s.pair.setAttribute("x", cx + slot / 2); s.pair.setAttribute("y", bottom + text * 2.35);
      s.pair.setAttribute("font-size", text);
      s.pair.textContent = bar.sound;
    }
  });
}

let last = 0;
function frame(now) {
  const dt = last ? Math.min(0.25, (now - last) / 1000) : 0;
  last = now;
  for (const [uid, m] of meters) {
    if (!m.rect.isConnected) { stopMeter(uid); continue; }
    try { drawMeter(m, now, dt); } catch (err) { console.warn("[vu]", err); stopMeter(uid); }
  }
  raf = meters.size ? requestAnimationFrame(frame) : 0;
  if (!raf) last = 0;
}

// ---- the cue ------------------------------------------------------------

function scoreSvg() {
  return document.querySelector("#scoreInner svg")
    || document.querySelector(".oscilla-score-inner svg")
    || document.querySelector("svg");
}

export function stopMeter(uid) {
  const m = meters.get(uid);
  if (!m) return;
  meters.delete(uid);
  m.g.remove();
  removeLiveAudioRect(uid);
}

const LAYOUT_WORDS = new Set(["stereo", "mono"]);

/** The words of vu(...): [names] -- stereo / mono are how, not what. */
export function vuSources(args = []) {
  const words = args.filter(a => a.type === "name").map(a => String(a.value)).filter(w => !LAYOUT_WORDS.has(w));
  return words.length ? [...new Set(words)] : ["master"];
}

/** vu(s3, stereo) / vu(all, stereo): pairs for the sounds it shows. */
export const vuStereo = (args = []) => args.some(a => a.type === "name" && String(a.value) === "stereo")
  || [true, 1, "1", "true"].includes(args.find(a => a.type === "stereo")?.value);

/**
 * The screen's right edge as far as the meters go: the live console docked
 * at the right covers it -- the meters then go left of it, or vu() seemed
 * to show nothing (in perform mode the code is over the score: the edge).
 */
function freeRight() {
  const p = document.getElementById("livecode-panel");
  if (!p || p.classList.contains("livecode-perform")) return window.innerWidth;
  const cs = getComputedStyle(p);
  if (cs.display === "none" || cs.visibility === "hidden") return window.innerWidth;
  const r = p.getBoundingClientRect();
  return r.width > 0 && r.right > window.innerWidth - 80 && r.left > window.innerWidth * 0.3 ? r.left : window.innerWidth;
}

/** vu(...) as a cue. */
export function handleVuCue(ast) {
  const args = ast?.args || [];
  const arg = k => args.find(a => a.type === k)?.value;
  const uid = String(arg("uid") ?? "vu");
  const stop = arg("stop"), off = arg("off");
  if ((stop != null && String(stop) !== "0") || (off != null && String(off) !== "0")) {
    if (arg("uid") == null) for (const u of [...meters.keys()]) stopMeter(u);
    else stopMeter(uid);
    return;
  }
  const sources = vuSources(args);
  const floor = Number(arg("floor"));
  const hold = Number(secs(arg("hold")));          // beats -> seconds
  const opts = {
    floor: Number.isFinite(floor) && floor < 0 ? floor : -60,
    hold: Number.isFinite(hold) && hold >= 0 ? hold : 1.5,
    stereo: vuStereo(args),
  };

  let m = meters.get(uid);
  if (m && !m.rect.isConnected) { stopMeter(uid); m = null; }   // the score was reloaded
  if (!m) {
    const svg = scoreSvg();
    if (!svg) return;
    const rect = createLiveAudioRect(svg, uid);
    if (!rect) return;
    rect._keepOnHush = true;          // hush() stops sounds; the meters stay
    // a narrow box at the screen's right edge, half its height: about one
    // bar's width per sound (screen px, into the score's units)
    // a pair for the mix, and for a sound shown in stereo
    const per = opts.stereo ? 2 : 1;
    const bars = sources.reduce((n, s) => n + (s === "master" ? 2 : s === "all" ? 4 * per : (vuModeOf(s) === "stereo" ? 2 : per)), 0);
    const ctm = svg.getScreenCTM();
    if (ctm) {
      const inv = ctm.inverse(), unit = 1 / Math.hypot(ctm.a, ctm.b);
      const wPx = Math.max(44, 22 * bars + 16), hPx = Math.max(140, window.innerHeight * 0.35);
      const pt = svg.createSVGPoint();
      pt.x = freeRight() - 40 - wPx; pt.y = (window.innerHeight - hPx) / 2;
      const p = pt.matrixTransform(inv);
      placeLiveRect(rect, { x: p.x, y: p.y, w: wPx * unit, h: hPx * unit });
    }
    const g = el("g", { class: "oscilla-vu" });
    g.dataset.vu = uid;
    const ground = el("rect", { fill: "#0b0d10", opacity: 0.9 }, g);
    rect.after(g);
    m = { rect, g, ground, sources, opts, bars: [], key: null };
    meters.set(uid, m);
  } else {
    m.sources = sources;
    m.opts = opts;
  }
  // size: and at: as viz() takes them -- fractions of the screen
  const size = arg("size"), at = arg("at");
  if (size != null || at != null) {
    const pass = [{ type: "uid", value: uid }];
    if (size != null) pass.push({ type: "size", value: size });
    if (at != null) pass.push({ type: "at", value: at });
    import("./viz.js").then(g => g.handleVizCue({ type: "cueViz", args: pass })).catch(err => console.warn("[vu]", err));
  }
  if (!raf) raf = requestAnimationFrame(frame);
}
